import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/elliott-towers", label: "Elliott Towers" },
  { to: "/investor-relations", label: "Investor Relations" },
  { to: "/media", label: "Press" },
  { to: "/app-ecosystem", label: "Ecosystem" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-background/85 backdrop-blur-xl border-b border-primary/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3 group"
          >
            <div className="flex items-center gap-2">
              <span className="font-serif text-2xl tracking-tight text-foreground">
                ELLIOTT
              </span>
              <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.3em] text-primary/80 border-l border-primary/30 pl-2">
                International<br />Development
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`navlink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-[13px] uppercase tracking-[0.18em] transition-colors hover:text-primary ${
                    isActive
                      ? "text-primary"
                      : "text-foreground/70"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{l.label}</span>
                    {isActive && (
                      <span className="absolute -bottom-2 left-0 right-0 h-px bg-primary" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user && user !== false ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/portal"}
                  data-testid="navbar-portal-link"
                  className="hidden md:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                >
                  Portal <ChevronRight className="w-3 h-3" />
                </Link>
                <button
                  data-testid="navbar-logout"
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="hidden md:inline-flex text-[12px] uppercase tracking-[0.2em] text-foreground/60 hover:text-foreground"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="navbar-login"
                className="hidden md:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-4 py-2 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
              >
                Investor Login
              </Link>
            )}
            <button
              data-testid="navbar-menu-toggle"
              className="lg:hidden text-foreground p-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-primary/10 bg-background/95 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-navlink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.18em] ${
                    isActive ? "text-primary" : "text-foreground/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="gold-divider my-2" />
            {user && user !== false ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/portal"}
                  className="text-primary uppercase tracking-[0.2em] text-sm"
                  data-testid="mobile-portal-link"
                >
                  Investor Portal
                </Link>
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  className="text-left text-foreground/60 uppercase tracking-[0.2em] text-sm"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="text-primary uppercase tracking-[0.2em] text-sm"
                data-testid="mobile-login-link"
              >
                Investor Login
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
